/**
 * The Tutorials section of the Ryerson Metal Resources landing: an image, a title, a description
 * and a button link to `/metal-resources/tutorials/`. Desktop puts the image and text side by
 * side; mobile stacks the description, the image and the link, with no title.
 *
 * Its only caller is `../MetalResourcesLandingRyerson.tsx`, which holds the copy
 * (`TutorialsContent`). The STS landing has no Tutorials section, and the tutorials page itself
 * is Ryerson only.
 */
import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { useTheme } from "@ryerson/frontend.theme";
import { ContentSection } from "@ryerson/frontend.layout";
import { Media } from "@ryerson/frontend.next";
import { Typography } from "@ryerson/frontend.typography";
import { ButtonLink } from "@ryerson/frontend.navigation";

/** One language's copy for the section. */
export type MetalResourcesTutorialsProps = {
	title: string;
	description: string;
	/** The button label, one line per entry (for example `["View", "All Tutorials"]`). */
	actionText: string[];
	actionLink: string;
	/** Site-relative image path, served from `application/public/`. */
	imageUrl: string;
};

const Left = styled.div`
	width: calc(50% - 20px);
	display: inline-block;
	vertical-align: top;
	margin-right: 20px;
`;

const Right = styled.div`
	width: calc(50% - 20px);
	display: inline-block;
	vertical-align: top;
	margin-left: 20px;
`;

const Text = styled.div`
	width: 80%;
	max-width: 460px;
`;

const ButtonLinkContainer = styled.div`
	margin-top: 47px;
	display: block;
	width: 100%;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media (max-width: ${theme.breakpoints.lg}) {
				margin-top: 44px;
				margin-bottom: 60px;
			}
		`;
	}}
`;

/** Renders the landing page's Tutorials section. */
const MetalResourcesTutorials: React.FC<MetalResourcesTutorialsProps> = ({
	title,
	description,
	actionText,
	actionLink,
	imageUrl,
}) => {
	const { theme } = useTheme();

	return (
		<>
			<Media greaterThanOrEqual="lg">
				<ContentSection vPadding="130px" type="primary">
					<Left>
						<img
							src={imageUrl}
							alt={title}
							css={css`
								width: 100%;
								height: auto;
							`}
						/>
					</Left>
					<Right>
						<Text
							css={css`
								margin-left: 17.66666%;
							`}
						>
							<Typography
								variant="h1"
								type="secondary"
								css={css`
									margin-top: 20px;
									margin-bottom: 56px;
								`}
							>
								{title}
							</Typography>
							<Typography
								size="md"
								type="secondary"
								css={css`
									display: block;
									max-width: 360px;
								`}
							>
								{description}
							</Typography>
							<ButtonLinkContainer theme={theme}>
								<ButtonLink to={actionLink} label={actionText} type="secondary" />
							</ButtonLinkContainer>
						</Text>
					</Right>
				</ContentSection>
			</Media>
			<Media lessThan="lg">
				<Typography
					variant="p"
					size="md"
					type="secondary"
					css={css`
						margin-top: 24px;
					`}
				>
					{description}
				</Typography>
				<img
					src={imageUrl}
					alt={description}
					css={css`
						width: 100%;
						height: auto;
						margin-top: 18px;
					`}
				/>
				<ButtonLinkContainer theme={theme}>
					<ButtonLink to={actionLink} label={actionText} type="secondary" />
				</ButtonLinkContainer>
			</Media>
		</>
	);
};

export default MetalResourcesTutorials;
